import type { LC, PropsWithChildren, LiveElement } from '@use-gpu/live';
import type { AggregatedCalls, PassBindGroup, PassFlags, PassResources } from '../pass/types';

import { use, yeet, memo, unquote, provide, multiGather, extend, useMemo, SUSPEND } from '@use-gpu/live';

import { PassContext, VariantContext, VariantContextProps } from '../providers/pass-provider';
import { QueueReconciler, PassReconciler } from '../reconcilers/index';

import { RenderComputePass } from '../pass/render-compute-pass';
import { DispatchPass } from '../pass/dispatch-pass';
import { ReadbackPass } from '../pass/readback-pass';

const {reconcile, quote} = PassReconciler;

const NO_ENV: Record<string, any> = {};

export type RendererProps = PropsWithChildren<{
  resources: PassResources,
  bindGroups: Record<string, PassBindGroup>,
  options: PassFlags,

  passes: LiveElement[],
  variants: VariantContextProps,

  decorate?: (children: LiveElement) => LiveElement,
}>;

const NO_DECORATE = (el: LiveElement) => el;

/**
  Materializes variants of draw calls for its children. (VariantContext)
  Multi-gathers the draw calls and process them with the given render passes. (PassContext)
*/
export const Renderer: LC<RendererProps> = memo((props: RendererProps) => {
  const {
    resources,
    bindGroups,
    options,

    passes,
    variants,

    decorate = NO_DECORATE,
    children,
  } = props;

  // Pass on shared render context(s) for renderables
  const passContext = useMemo(() => ({...resources, bindGroups, options}), [resources, bindGroups, options]);

  // Pass aggregrated calls to pass runners
  const Resume = (
    callsOrSuspend: AggregatedCalls | typeof SUSPEND,
  ) =>
    useMemo(() => {
      if (callsOrSuspend === SUSPEND) {
        return QueueReconciler.quote(yeet(SUSPEND));
      }
      const calls = callsOrSuspend as AggregatedCalls;

      const env = calls.env?.reduce((env: Record<string, any>, data: Record<string, any>) => {
        for (const k in data) env[k] = data[k];
        return env;
      }, {}) ?? NO_ENV;

      const props: Record<string, any> = {calls, env};

      return [
        calls.dispatch ? use(DispatchPass, props) : null,
        calls.pre || calls.compute ? use(RenderComputePass, props) : null,
        ...passes.map(element => extend(element, props)),
        calls.post || calls.readback ? use(ReadbackPass, props) : null,
      ];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callsOrSuspend, passes]);

  return (
    reconcile(
      quote(
        provide(PassContext, passContext,
          decorate(multiGather(
            unquote(provide(VariantContext, variants, children)),
            Resume,
            SUSPEND,
          ))
        )
      )
    )
  );
}, 'Renderer');
